import React, { useState, useMemo, useRef } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

/**
 * Hook voor sorteerbare data.
 * Gebruik:
 *   const { sortedRows, sortKey, sortDir, toggleSort, SortHeader } = useSortable(rows, "naam");
 *   <table>
 *     <thead><tr>
 *       <SortHeader k="naam">Naam</SortHeader>
 *       <SortHeader k="bedrag" align="right" accessor={r => Number(r.bedrag||0)}>Bedrag</SortHeader>
 *     </tr></thead>
 *     <tbody>{sortedRows.map(...)}</tbody>
 *   </table>
 */
export function useSortable(rows, defaultKey = null, defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  // Accessors in een ref bewaren — schrijven tijdens render veroorzaakt GEEN re-render
  // (een functie als state opslaan zou een oneindige render-loop geven).
  const accessorsRef = useRef({});

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k); setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const acc = accessorsRef.current[sortKey] || ((r) => r?.[sortKey]);
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const va = acc(a), vb = acc(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // nulls last
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      // Try parse dates
      const isDate = (x) => typeof x === "string" && /^\d{4}-\d{2}-\d{2}/.test(x);
      if (isDate(va) && isDate(vb)) return va.localeCompare(vb) * dir;
      return String(va).localeCompare(String(vb), "nl", { numeric: true, sensitivity: "base" }) * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const SortHeader = ({ k, children, align = "left", accessor, className = "" }) => {
    if (accessor) accessorsRef.current[k] = accessor;
    const active = sortKey === k;
    const Icon = !active ? ChevronsUpDown : (sortDir === "asc" ? ChevronUp : ChevronDown);
    return (
      <th className={`text-${align} p-3 ${className}`}>
        <button type="button" onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? "text-foreground" : "text-muted-foreground"}`}
          data-testid={`sort-${k}`}>
          {children}
          <Icon className={`h-3 w-3 ${active ? "opacity-100" : "opacity-50"}`} />
        </button>
      </th>
    );
  };

  return { sortedRows, sortKey, sortDir, toggleSort, SortHeader };
}
